/* SocialMediaAdmin.jsx - Admin/Manager panel: settings, QR, consent list */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Share2, QrCode, FileSpreadsheet, Save, RefreshCw, Trash2, Download, Globe, Youtube, Instagram, Music2, Facebook, Copy, ExternalLink } from 'lucide-react';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SocialMediaAdmin() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [tab, setTab] = useState('settings');
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [consents, setConsents] = useState([]);
    const [qrUrl, setQrUrl] = useState('');
    const [qrLoading, setQrLoading] = useState(false);
    const [publicUrl, setPublicUrl] = useState('');

    useEffect(() => {
        loadSettings();
        loadConsents();
        loadQr();
        // Public URL: derived from window location (frontend domain) + /social
        setPublicUrl(`${window.location.origin}/social`);
    }, []);

    const loadSettings = async () => {
        try {
            const res = await api.get('/social/settings');
            setSettings(res.data);
        } catch (e) {
            toast.error('Ayarlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const loadConsents = async () => {
        try {
            const res = await api.get('/social/consents');
            setConsents(res.data.items || []);
        } catch (e) {
            console.error(e);
        }
    };

    const loadQr = async () => {
        setQrLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/social/qr`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const blob = await res.blob();
                if (qrUrl) URL.revokeObjectURL(qrUrl);
                setQrUrl(URL.createObjectURL(blob));
            }
        } catch (e) {
            console.error(e);
        } finally {
            setQrLoading(false);
        }
    };

    const handleField = (key, value) => {
        setSettings((s) => ({ ...s, [key]: value }));
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const res = await api.put('/social/settings', settings);
            setSettings(res.data);
            toast.success('Ayarlar kaydedildi');
            // Re-fetch QR in case base URL changed
            loadQr();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Kaydetme başarısız');
        } finally {
            setSaving(false);
        }
    };

    const exportExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/social/consents/export`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kvkk_riza_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('Excel indirildi');
        } catch (e) {
            toast.error('Excel export başarısız');
        }
    };

    const deleteConsent = async (id) => {
        if (!window.confirm('Bu rıza kaydını silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/social/consents/${id}`);
            toast.success('Silindi');
            loadConsents();
        } catch (e) {
            toast.error('Silme başarısız');
        }
    };

    const copyUrl = () => {
        navigator.clipboard.writeText(publicUrl);
        toast.success('Bağlantı panoya kopyalandı');
    };

    const downloadQr = () => {
        if (!qrUrl) return;
        const a = document.createElement('a');
        a.href = qrUrl;
        a.download = 'grand_kaptan_social_qr.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const printQr = () => {
        if (!qrUrl) return;
        const w = window.open('', '_blank');
        if (!w) return;
        w.document.write(`
            <html><head><title>Grand Kaptan - Social QR</title>
            <style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#fff}
            h1{margin:0 0 10px;color:#1e3a5f}
            p{color:#475569;margin:6px 0}
            img{width:380px;height:380px;border:1px solid #e2e8f0;border-radius:14px;padding:14px}
            </style></head><body>
            <h1>Grand Kaptan Hotel</h1>
            <p>Sosyal Medya & Video Çekimi Onayı</p>
            <img src="${qrUrl}" />
            <p style="margin-top:10px;font-size:12px">${publicUrl}</p>
            <script>window.onload=()=>window.print();</script>
            </body></html>`);
        w.document.close();
    };

    if (loading || !settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-orwion-green" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="social-admin-page">
            {/* Header */}
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                    <Share2 className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Sosyal Medya & KVKK</h1>
                    <p className="text-sm text-slate-500">QR kod, sosyal medya linkleri ve rıza onay yönetimi</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="settings" data-testid="tab-settings">Ayarlar</TabsTrigger>
                    <TabsTrigger value="qr" data-testid="tab-qr">QR Kod</TabsTrigger>
                    <TabsTrigger value="consents" data-testid="tab-consents">
                        Rıza Onayları
                        <Badge variant="outline" className="ml-2">{consents.length}</Badge>
                    </TabsTrigger>
                </TabsList>

                {/* ===== AYARLAR ===== */}
                <TabsContent value="settings" className="space-y-4 mt-4">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Globe className="w-5 h-5 text-blue-600" /> Sosyal Medya Linkleri
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <SocialField icon={Youtube} color="text-red-600" label="YouTube"
                                value={settings.youtube_url} onChange={(v) => handleField('youtube_url', v)}
                                placeholder="https://youtube.com/@grandkaptanhotel" testId="youtube-input" disabled={!isAdmin} />
                            <SocialField icon={Instagram} color="text-pink-600" label="Instagram"
                                value={settings.instagram_url} onChange={(v) => handleField('instagram_url', v)}
                                placeholder="https://instagram.com/grandkaptanhotel" testId="instagram-input" disabled={!isAdmin} />
                            <SocialField icon={Music2} color="text-slate-800" label="TikTok"
                                value={settings.tiktok_url} onChange={(v) => handleField('tiktok_url', v)}
                                placeholder="https://tiktok.com/@grandkaptanhotel" testId="tiktok-input" disabled={!isAdmin} />
                            <SocialField icon={Facebook} color="text-blue-600" label="Facebook"
                                value={settings.facebook_url} onChange={(v) => handleField('facebook_url', v)}
                                placeholder="https://facebook.com/grandkaptanhotel" testId="facebook-input" disabled={!isAdmin} />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Karşılama Başlığı (3 Dil)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <LangInput langLabel="Türkçe 🇹🇷" titleKey="welcome_title_tr" subKey="welcome_subtitle_tr" settings={settings} onChange={handleField} disabled={!isAdmin} />
                            <LangInput langLabel="English 🇬🇧" titleKey="welcome_title_en" subKey="welcome_subtitle_en" settings={settings} onChange={handleField} disabled={!isAdmin} />
                            <LangInput langLabel="Русский 🇷🇺" titleKey="welcome_title_ru" subKey="welcome_subtitle_ru" settings={settings} onChange={handleField} disabled={!isAdmin} />
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">KVKK Rıza Metinleri</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <KvkkArea langLabel="Türkçe 🇹🇷" valueKey="kvkk_text_tr" settings={settings} onChange={handleField} disabled={!isAdmin} />
                            <KvkkArea langLabel="English 🇬🇧" valueKey="kvkk_text_en" settings={settings} onChange={handleField} disabled={!isAdmin} />
                            <KvkkArea langLabel="Русский 🇷🇺" valueKey="kvkk_text_ru" settings={settings} onChange={handleField} disabled={!isAdmin} />
                        </CardContent>
                    </Card>

                    {isAdmin && (
                        <div className="sticky bottom-4 flex justify-end">
                            <Button onClick={saveSettings} disabled={saving} size="lg" className="bg-orwion-green hover:bg-orwion-green-hover shadow-lg" data-testid="save-settings-btn">
                                <Save className="w-5 h-5 mr-2" />
                                {saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                            </Button>
                        </div>
                    )}
                    {!isAdmin && (
                        <p className="text-xs text-slate-500 italic">Ayarları yalnızca Admin değiştirebilir.</p>
                    )}
                </TabsContent>

                {/* ===== QR ===== */}
                <TabsContent value="qr" className="mt-4">
                    <Card>
                        <CardContent className="p-6">
                            <div className="grid md:grid-cols-2 gap-6 items-center">
                                <div className="flex flex-col items-center">
                                    <div className="bg-white rounded-2xl border-2 border-slate-200 p-4 shadow-md">
                                        {qrLoading || !qrUrl ? (
                                            <div className="w-72 h-72 flex items-center justify-center">
                                                <RefreshCw className="w-8 h-8 animate-spin text-slate-300" />
                                            </div>
                                        ) : (
                                            <img src={qrUrl} alt="QR" className="w-72 h-72" data-testid="qr-image" />
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <h2 className="text-xl font-bold text-slate-800">Misafir QR Kodu</h2>
                                        <p className="text-sm text-slate-500 mt-1">
                                            Misafirleriniz bu QR'ı okuttuğunda dilini seçer, sosyal medya hesaplarımıza ulaşır
                                            ve KVKK rıza onayını verir.
                                        </p>
                                    </div>
                                    <div className="bg-slate-50 border rounded-lg p-3 flex items-center gap-2">
                                        <code className="flex-1 text-xs text-slate-700 truncate" data-testid="public-url">{publicUrl}</code>
                                        <Button variant="ghost" size="icon" onClick={copyUrl} data-testid="copy-url-btn">
                                            <Copy className="w-4 h-4" />
                                        </Button>
                                        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-900">
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button onClick={downloadQr} disabled={!qrUrl} data-testid="qr-download-btn">
                                            <Download className="w-4 h-4 mr-2" /> İndir (PNG)
                                        </Button>
                                        <Button onClick={printQr} disabled={!qrUrl} variant="outline" data-testid="qr-print-btn">
                                            <QrCode className="w-4 h-4 mr-2" /> Yazdırılabilir Görünüm
                                        </Button>
                                        <Button onClick={loadQr} variant="outline" size="icon">
                                            <RefreshCw className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ===== CONSENTS ===== */}
                <TabsContent value="consents" className="mt-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <CardTitle className="text-lg">Rıza Onayları ({consents.length})</CardTitle>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={loadConsents} data-testid="refresh-consents-btn">
                                        <RefreshCw className="w-4 h-4 mr-1" /> Yenile
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel-btn">
                                        <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Ad Soyad</TableHead>
                                        <TableHead>Oda No</TableHead>
                                        <TableHead>Dil</TableHead>
                                        <TableHead>Tarih</TableHead>
                                        <TableHead>IP</TableHead>
                                        {isAdmin && <TableHead className="text-right">İşlem</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {consents.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={isAdmin ? 6 : 5} className="text-center py-8 text-slate-400 text-sm">
                                                Henüz rıza onayı yok
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {consents.map((c) => (
                                        <TableRow key={c.id} data-testid={`consent-row-${c.id}`}>
                                            <TableCell className="font-medium">{c.full_name}</TableCell>
                                            <TableCell>
                                                <Badge variant="outline">{c.room_number}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
                                                    {c.language === 'tr' ? '🇹🇷 TR' : c.language === 'en' ? '🇬🇧 EN' : c.language === 'ru' ? '🇷🇺 RU' : c.language}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm text-slate-600">
                                                {c.created_at ? new Date(c.created_at).toLocaleString('tr-TR') : '-'}
                                            </TableCell>
                                            <TableCell className="text-xs text-slate-400">{c.ip_address || '-'}</TableCell>
                                            {isAdmin && (
                                                <TableCell className="text-right">
                                                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700"
                                                        onClick={() => deleteConsent(c.id)} data-testid={`delete-consent-${c.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function SocialField({ icon: Icon, color, label, value, onChange, placeholder, testId, disabled }) {
    return (
        <div>
            <label className="text-xs font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                <Icon className={`w-4 h-4 ${color}`} /> {label}
            </label>
            <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} data-testid={testId} />
        </div>
    );
}

function LangInput({ langLabel, titleKey, subKey, settings, onChange, disabled }) {
    return (
        <div className="border rounded-lg p-3 space-y-2 bg-slate-50/40">
            <div className="text-xs font-semibold text-slate-700">{langLabel}</div>
            <Input value={settings[titleKey] || ''} onChange={(e) => onChange(titleKey, e.target.value)} placeholder="Başlık" disabled={disabled} />
            <Input value={settings[subKey] || ''} onChange={(e) => onChange(subKey, e.target.value)} placeholder="Alt başlık" disabled={disabled} />
        </div>
    );
}

function KvkkArea({ langLabel, valueKey, settings, onChange, disabled }) {
    return (
        <div className="border rounded-lg p-3 space-y-2 bg-slate-50/40">
            <div className="text-xs font-semibold text-slate-700">{langLabel}</div>
            <textarea
                value={settings[valueKey] || ''}
                onChange={(e) => onChange(valueKey, e.target.value)}
                rows={8}
                disabled={disabled}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-orwion-green/30 disabled:bg-slate-100 disabled:cursor-not-allowed"
                data-testid={`kvkk-textarea-${valueKey}`}
            />
        </div>
    );
}
